import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  useTheme,
  Tab,
  Tabs,
  TextField,
  Button,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  InputAdornment,
  Alert,
  Snackbar,
  Grid,
  IconButton,
  Badge,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
} from "@mui/material";
import Header from "components/Header";
import {
  AccountBalanceWalletOutlined,
  TrendingUpOutlined,
  TrendingDownOutlined,
  ReceiptLongOutlined,
  AddCircleOutline,
  PaymentOutlined,
  CategoryOutlined,
  ChecklistOutlined,
  DeleteOutline,
  AddShoppingCartOutlined,
  SendOutlined,
  KeyboardArrowDown,
  KeyboardArrowUp,
  EditOutlined,
  CheckOutlined,
  CheckBoxOutlined,
  CalendarMonthOutlined,
  Close as CloseIcon,
  PrintOutlined,
} from "@mui/icons-material";
import formatRupiah from "utils/formatRupiah";
import moment from "moment";
import "moment/locale/id";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteListTransaksiWarkat,
  realisasiListTransaksiWarkat,
  fetchListPerkiraan,
  fetchListTransaksi,
  fetchListTransaksiWarkat,
  postListTransaksiWarkat,
  editTanggalListTransaksiWarkat,
} from "state/alokasiAction";
import { getSaldoKasSaatIni } from "./kasBesarUtils";
import FloatingAlert from "components/FloatingAlert";
import { setMessage, setStatus } from "state";
import KonfirmasiModal from "components/Modals/KonfirmasiModal";
import UbahTanggalModal from "components/Modals/UbahTanggalModal";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
moment.locale("id");

const KasBesar = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  // Tab pengeluaran
  const [tabValue, setTabValue] = useState(0);

  // Alokasi (Cart)
  const [alokasi, setAlokasi] = useState([]);
  const [tanggalAlokasi, setTanggalAlokasi] = useState(moment());

  const [selectedTransaksi, setSelectedTransaksi] = useState(null);
  const [selectedTransaksis, setSelectedTransaksis] = useState([]);
  const [nominalBayar, setNominalBayar] = useState("");
  const [nominalBayarMulti, setNominalBayarMulti] = useState({});

  useEffect(() => {
    if (selectedTransaksis.length > 0) {
      const total = selectedTransaksis.reduce((sum, item) => {
        const nominal = nominalBayarMulti[item.no_bukti_penerimaan] || 0;
        return sum + nominal;
      }, 0);
      setNominalBayar(total > 0 ? total.toLocaleString("id-ID") : "");
    }
  }, [nominalBayarMulti, selectedTransaksis]);

  const [selectedTipe, setSelectedTipe] = useState(null);
  const [namaTransaksi, setNamaTransaksi] = useState("");
  const [nominalLainnya, setNominalLainnya] = useState("");
  const [openKonfirmasi, setOpenKonfirmasi] = useState(false);
  const [tanggalPengganti, setTanggalPengganti] = useState(null);
  const [openUbahTanggal, setOpenUbahTanggal] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [expandedRows, setExpandedRows] = useState({});
  const { list_transaksi_warkat, list_transaksi, list_perkiraan } = useSelector(
    (state) => state.alokasi,
  );
  const { status, message } = useSelector((state) => state.global);
  
  const getInit = () => {
    dispatch(fetchListTransaksiWarkat());
    dispatch(fetchListTransaksi());
    dispatch(fetchListPerkiraan());
  };
  
  useEffect(() => {
    getInit();
  }, []);

  // Modal Pilih Transaksi
  const [openPilihTransaksi, setOpenPilihTransaksi] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState(moment().startOf("month"));
  const [endDate, setEndDate] = useState(moment().endOf("month"));
  const [startJatuhTempo, setStartJatuhTempo] = useState(null);
  const [endJatuhTempo, setEndJatuhTempo] = useState(null);

  // Filtered transactions based on due date
  const filteredListTransaksi = useMemo(() => {
    if (!list_transaksi || !Array.isArray(list_transaksi)) return [];
    
    let result = [...list_transaksi];

    // Filter by date range (jatuh tempo = tanggal_transaksi + TOP)
    if (startJatuhTempo && endJatuhTempo) {
      result = result.filter(item => {
        const tglTransaksi = moment(item.tanggal_transaksi);
        const top = parseInt(item.top) || 0;
        const tglJatuhTempo = tglTransaksi.add(top, "days");
        return tglJatuhTempo.isSameOrAfter(startJatuhTempo, "day") && tglJatuhTempo.isSameOrBefore(endJatuhTempo, "day");
      });
    }

    // Filter by search text
    if (searchText) {
      const lower = searchText.toLowerCase();
      result = result.filter(item =>
        (item.nama_supplier || "").toLowerCase().includes(lower) ||
        (item.no_bukti_penerimaan || "").toLowerCase().includes(lower)
      );
    }

    // Sort by due date (closest first)
    result.sort((a, b) => {
      const aTop = parseInt(a.top) || 0;
      const bTop = parseInt(b.top) || 0;
      const aJatuhTempo = moment(a.tanggal_transaksi).add(aTop, "days");
      const bJatuhTempo = moment(b.tanggal_transaksi).add(bTop, "days");
      return aJatuhTempo.valueOf() - bJatuhTempo.valueOf();
    });

    return result;
  }, [list_transaksi, searchText, startJatuhTempo, endJatuhTempo]);

  const handlePilihTransaksi = (item) => {
    setSelectedTransaksi(item);
    setSelectedTransaksis([]);
    setNominalBayarMulti({});
    setNominalBayar(parseInt(item.sisa_hutang).toLocaleString("id-ID"));
    setOpenPilihTransaksi(false);
    setSearchText("");
    setStartJatuhTempo(null);
    setEndJatuhTempo(null);
  };

  const handleToggleTransaksi = (item) => {
    setSelectedTransaksis((prev) => {
      const exists = prev.find((t) => t.no_bukti_penerimaan === item.no_bukti_penerimaan);
      if (exists) {
        return prev.filter((t) => t.no_bukti_penerimaan !== item.no_bukti_penerimaan);
      }
      return [...prev, item];
    });
  };

  const handlePilihMultiple = () => {
    if (selectedTransaksis.length === 0) return;
    const initialNominals = {};
    selectedTransaksis.forEach((item) => {
      initialNominals[item.no_bukti_penerimaan] = parseInt(item.sisa_hutang) || 0;
    });
    setNominalBayarMulti(initialNominals);
    const totalSisa = selectedTransaksis.reduce((sum, item) => sum + (parseInt(item.sisa_hutang) || 0), 0);
    setNominalBayar(totalSisa.toLocaleString("id-ID"));
    setSearchText("");
    setStartJatuhTempo(null);
    setEndJatuhTempo(null);
    setOpenPilihTransaksi(false);
  };

  const handleResetFilterTransaksi = () => {
    setSearchText("");
    setStartJatuhTempo(null);
    setEndJatuhTempo(null);
  };



  const handleCloseSnackbar = () => {
    setSelectedTransaksi(null);
    setTanggalPengganti(null);
    dispatch(setStatus("default"));
    dispatch(setMessage(""));
    dispatch(fetchListTransaksiWarkat());
    setAlokasi([]);
  };

  const handleDelete = () => {
    dispatch(deleteListTransaksiWarkat(selectedTransaksi.id));
    setOpenKonfirmasi(false);
    setSelectedTransaksi(null);
    setTanggalPengganti(null);
  };

  const handleUbahTanggal = (tanggalBaru) => {
    dispatch(
      editTanggalListTransaksiWarkat({
        id: selectedTransaksi.id,
        tanggalnew: tanggalBaru,
      }),
    );
    setOpenUbahTanggal(false);
    setTanggalPengganti(null);
  };

  const handlePrintAlokasi = (row) => {
    const dataToPrint = row.items || [row.singleItem];
    sessionStorage.setItem(
      "datacetakalokasi",
      JSON.stringify({
        data: dataToPrint,
        tanggal: row.tanggal,
        tipe: row.tipe
      })
    );
    const printWindow = window.open("/cetak-alokasi", "_blank");
    if (printWindow) printWindow.focus();
  };

  const riwayatGrouped = useMemo(() => {
    if (
      !Array.isArray(list_transaksi_warkat) ||
      list_transaksi_warkat.length === 0
    )
      return [];

    // Filter by date range
    const filteredByDate = list_transaksi_warkat.filter((item) => {
      const tgl = moment(item.tanggal);
      return tgl.isSameOrAfter(startDate, "day") && tgl.isSameOrBefore(endDate, "day");
    });

    // Group by tanggal + tipe
    const groups = {};
    filteredByDate.forEach((item) => {
      const key = `${item.tanggal}_${item.tipe}`;
      if (!groups[key]) {
        groups[key] = {
          tanggal: item.tanggal,
          tipe: item.tipe,
          status: item.status,
          items: [],
          totalNominal: 0,
        };
      }
      groups[key].items.push(item);
      groups[key].totalNominal += Number(item.nominal) || 0;
    });

    // Convert to array and sort by date descending
    return Object.values(groups)
      .sort((a, b) => moment(b.tanggal).diff(moment(a.tanggal)))
      .map((group, idx) => ({
        id: `group_${idx}`,
        tanggal: group.tanggal,
        tipe: group.tipe,
        status: group.status,
        nominal: group.totalNominal,
        keterangan:
          group.items.length === 1
            ? group.items[0].nama
            : `${group.tipe === "pemasukan" ? "Pemasukan" : "Pengeluaran"} (${group.items.length} item)`,
        items: group.items.length > 1 ? group.items : null,
        singleItem: group.items.length === 1 ? group.items[0] : null,
      }));
  }, [list_transaksi_warkat, startDate, endDate]);

  // ── Computed values ──────────────────────────────────────────────────────

  const kasInfo = useMemo(() => {
    const data = Array.isArray(list_transaksi_warkat)
      ? list_transaksi_warkat
      : [];
    const saldoSaatIni = getSaldoKasSaatIni(data);

    // Apply time filter
    const filteredData = data.filter((r) => {
      const tgl = moment(r.tanggal);
      return tgl.isSameOrAfter(startDate, "day") && tgl.isSameOrBefore(endDate, "day");
    });

    const pendapatan = filteredData
      .filter((r) => r.tipe === "pemasukan" && r.status === "realisasi")
      .reduce((sum, r) => sum + (Number(r.nominal) || 0), 0);

    const alokasi = filteredData
      .filter((r) => r.tipe === "pengeluaran" && r.status === "alokasi")
      .reduce((sum, r) => sum + (Number(r.nominal) || 0), 0);

    const pengeluaran = filteredData
      .filter((r) => r.tipe === "pengeluaran" && r.status === "realisasi")
      .reduce((sum, r) => sum + (Number(r.nominal) || 0), 0);

    const selisih = pendapatan - pengeluaran;

    return {
      saldoSaatIni,
      pendapatan,
      alokasi,
      pengeluaran,
      selisih,
    };
  }, [list_transaksi_warkat, startDate, endDate]);

  const totalAlokasi = useMemo(() => {
    return alokasi.reduce((sum, item) => sum + item.nominal, 0);
  }, [alokasi]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  // Tambah ke alokasi dari Pengeluaran Terdaftar
  const handleTambahAlokasiTerdaftar = () => {
    if (!selectedTransaksi && selectedTransaksis.length === 0) {
      setSnackbar({
        open: true,
        message: "Pilih transaksi yang akan dibayar terlebih dahulu.",
        severity: "warning",
      });
      return;
    }
    const nominal = parseInt(nominalBayar.replace(/\D/g, ""), 10);
    if (!nominal || nominal <= 0) {
      setSnackbar({
        open: true,
        message: "Masukkan nominal yang valid.",
        severity: "warning",
      });
      return;
    }

    // Handle multiple selection
    if (selectedTransaksis.length > 0) {
      const newItems = selectedTransaksis
        .map((item) => {
          const nominalItem = nominalBayarMulti[item.no_bukti_penerimaan] || 0;
          const sisaHutang = parseInt(item.sisa_hutang) || 0;
          if (nominalItem > sisaHutang) {
            setSnackbar({
              open: true,
              message: `Nominal untuk ${item.no_bukti_penerimaan} tidak boleh melebihi sisa hutang ${formatRupiah(sisaHutang)}.`,
              severity: "error",
            });
            return null;
          }
          if (nominalItem <= 0) return null;
          return {
            id: Date.now() + Math.random(),
            keterangan: `${item.no_bukti_penerimaan} - ${item.nama_supplier}`,
            nominal: nominalItem,
            sumber: "Terdaftar",
            no_bukti: item.no_bukti_penerimaan,
            id_supplier: item.id_supplier,
            nama_supplier: item.nama_supplier,
          };
        })
        .filter(Boolean);

      if (newItems.length === 0) {
        setSnackbar({
          open: true,
          message: "Masukkan nominal yang valid untuk setidaknya satu transaksi.",
          severity: "warning",
        });
        return;
      }

      setAlokasi((prev) => [...prev, ...newItems]);
      setSelectedTransaksis([]);
      setNominalBayar("");
      setNominalBayarMulti({});
      setSnackbar({
        open: true,
        message: `${newItems.length} transaksi ditambahkan ke alokasi.`,
        severity: "success",
      })
      return;
    }

    // Handle single selection
    if (nominal > parseInt(selectedTransaksi.sisa_hutang)) {
      setSnackbar({
        open: true,
        message: `Nominal pembayaran tidak boleh melebihi sisa hutang ${formatRupiah(parseInt(selectedTransaksi.sisa_hutang))}.`,
        severity: "error",
      });
      return;
    }

    const newItem = {
      id: Date.now(),
      keterangan: `${selectedTransaksi.no_bukti_penerimaan} - ${selectedTransaksi.nama_supplier}`,
      nominal: nominal,
      sumber: "Terdaftar",
      no_bukti: selectedTransaksi.no_bukti_penerimaan,
      id_supplier: selectedTransaksi.id_supplier,
      nama_supplier: selectedTransaksi.nama_supplier,
    };

    setAlokasi((prev) => [...prev, newItem]);
    setSelectedTransaksi(null);
    setNominalBayar("");
    setSnackbar({
      open: true,
      message: `"${newItem.keterangan}" ditambahkan ke alokasi.`,
      severity: "success",
    });
  };

  // Tambah ke alokasi dari Pengeluaran Lainnya
  const handleTambahAlokasiLainnya = () => {
    if (!selectedTipe) {
      setSnackbar({
        open: true,
        message: "Pilih tipe pengeluaran terlebih dahulu.",
        severity: "warning",
      });
      return;
    }
    if (!namaTransaksi.trim()) {
      setSnackbar({
        open: true,
        message: "Masukkan nama transaksi.",
        severity: "warning",
      });
      return;
    }
    const nominal = parseInt(nominalLainnya.replace(/\D/g, ""), 10);
    if (!nominal || nominal <= 0) {
      setSnackbar({
        open: true,
        message: "Masukkan nominal yang valid.",
        severity: "warning",
      });
      return;
    }

    const newItem = {
      id: Date.now(),
      keterangan: `[${selectedTipe.kode_perkiraan}] ${namaTransaksi}`,
      nominal: nominal,
      sumber: "Lainnya",
      // Metadata untuk API request
      kode_perkiraan: selectedTipe.kode_perkiraan,
      nama_perkiraan: selectedTipe.nama_perkiraan || "",
      nama_transaksi: namaTransaksi,
    };

    setAlokasi((prev) => [...prev, newItem]);
    setSelectedTipe(null);
    setNamaTransaksi("");
    setNominalLainnya("");
    setSnackbar({
      open: true,
      message: `"${namaTransaksi}" ditambahkan ke alokasi.`,
      severity: "success",
    });
  };

  // Hapus item dari alokasi
  const handleHapusDariAlokasi = (itemId) => {
    setAlokasi((prev) => prev.filter((item) => item.id !== itemId));
  };

  // Edit item alokasi — kembalikan ke form kiri
  const handleEditAlokasi = (item) => {
    // Hapus dari alokasi
    setAlokasi((prev) => prev.filter((i) => i.id !== item.id));

    if (item.sumber === "Terdaftar") {
      // Cari transaksi yang cocok di list_transaksi
      const found = list_transaksi.find(
        (t) => t.no_bukti_penerimaan === item.keterangan.split(" - ")[0],
      );
      setSelectedTransaksi(found || null);
      setNominalBayar(item.nominal.toLocaleString("id-ID"));
      setTabValue(0);
    } else {
      // Parse keterangan dadakan: "[Tipe] Nama"
      const match = item.keterangan.match(/^\[(.+?)\]\s*(.*)$/);
      if (match) {
        const tipeLabel = match[1];
        const nama = match[2];
        const foundTipe = list_perkiraan.find(
          (t) => t.kode_perkiraan === tipeLabel,
        );
        setSelectedTipe(foundTipe || { kode_perkiraan: tipeLabel });
        setNamaTransaksi(nama);
      } else {
        setSelectedTipe(null);
        setNamaTransaksi(item.keterangan);
      }
      setNominalLainnya(item.nominal.toLocaleString("id-ID"));
      setTabValue(1);
    }

    setSnackbar({
      open: true,
      message: "Item dikembalikan ke form untuk diedit.",
      severity: "info",
    });
  };

  // Proses semua item di alokasi
  const handleProsesAlokasi = async () => {
    if (alokasi.length === 0) {
      setSnackbar({
        open: true,
        message: "Alokasi masih kosong.",
        severity: "warning",
      });
      return;
    }
    // if (totalAlokasi > kasInfo.saldoSaatIni) {
    //   setSnackbar({
    //     open: true,
    //     message: `Total alokasi ${formatRupiah(totalAlokasi)} melebihi saldo kas ${formatRupiah(kasInfo.saldoSaatIni)}. Silakan kurangi item.`,
    //     severity: "error",
    //   });
    //   return;
    // }

    const requests = alokasi.map((item) => {
      if (item.sumber === "Terdaftar") {
        // Kontrabon — punya no_bukti
        return {
          nama: `Pembayaran Kontrabon ${item.no_bukti}`,
          tanggal: moment(tanggalAlokasi).format(),
          nominal: item.nominal,
          no_bukti: item.no_bukti,
          catatan: `Pembayaran Kontrabon`,
        };
      } else {
        // Lainnya — punya kode_perkiraan
        return {
          nama: item.nama_transaksi || item.keterangan,
          tanggal: moment(tanggalAlokasi).format(),
          nominal: item.nominal,
          kode_perkiraan: item.kode_perkiraan,
          catatan: item.nama_transaksi || item.keterangan,
        };
      }
    });

    await dispatch(postListTransaksiWarkat(requests));
  };

  const handleNominalChange = (setter) => (e) => {
    const raw = e.target.value.replace(/\D/g, "");
    if (raw === "") {
      setter("");
      return;
    }
    const formatted = parseInt(raw, 10).toLocaleString("id-ID");
    setter(formatted);
  };

  // ── Styles ───────────────────────────────────────────────────────────────

  const cardStyle = {
    p: 3,
    borderRadius: "12px",
    backgroundColor: theme.palette.background.alt,
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    transition: "box-shadow 0.2s ease",
    "&:hover": {
      boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
    },
  };
  const styles = {
    btnRealisasi: {
      ml: 1,
      borderRadius: 8,
      fontSize: 12,
      color: "#FFF",
      backgroundColor: "rgb(32, 164, 80)", // Hijau
      "&:hover": {
        backgroundColor: "rgb(32, 164, 80)", // Hijau
      },
    },
    btnHapus: {
      ml: 1,
      borderRadius: 8,
      fontSize: 12,
      color: "#ffff", // Merah
      backgroundColor: "#f6041c", // Pink muda (lebih profesional dari 'pink')
      "&:hover": {
        backgroundColor: "#f6041c", // Pink muda (lebih profesional dari 'pink')
      },
    },
    btnUbah: {
      ml: 1,
      borderRadius: 8, // Diseragamkan dengan lainnya
      fontSize: 12,
      color: "#FFF",
      backgroundColor: theme.palette.primary.dark,
      padding: "4px 8px",
      "&:hover": {
      backgroundColor: theme.palette.primary.dark,
      },
    },
  };

  const statCardStyle = {
    ...cardStyle,
    display: "flex",
    flexDirection: "column",
    gap: 1,
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <Box m="1.5rem 2.5rem" pb="2rem">
      <Header
        title="Kas Besar"
        subtitle="Kelola dan pantau arus keuangan perusahaan."
      />

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 1: Informasi Kas Perusahaan
          ══════════════════════════════════════════════════════════════════════ */}
      <Box mt="2rem">
        <Box display="flex" justifyContent="space-between" alignItems="center" mb="1rem">
          <Typography
            variant="h5"
            fontWeight="600"
            color={theme.palette.secondary[100]}
            display="flex"
            alignItems="center"
            gap={1}
          >
            <AccountBalanceWalletOutlined /> Informasi Kas Perusahaan
          </Typography>
          
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DatePicker
                label="Tanggal Mulai"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{
                  textField: { size: "small", sx: { width: 160, backgroundColor: theme.palette.background.alt, borderRadius: 1 } },
                }}
              />
              <Typography>-</Typography>
              <DatePicker
                label="Tanggal Akhir"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{
                  textField: { size: "small", sx: { width: 160, backgroundColor: theme.palette.background.alt, borderRadius: 1 } },
                }}
              />
            </Box>
          </LocalizationProvider>
        </Box>

        {/* Saldo Utama */}
        <Paper
          sx={{
            ...cardStyle,
            mb: 2,
            background: `linear-gradient(135deg, ${theme.palette.secondary[500]} 0%, ${theme.palette.secondary[700]} 100%)`,
            color: "#fff",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              right: -20,
              top: -20,
              opacity: 0.1,
              fontSize: "180px",
            }}
          >
            <AccountBalanceWalletOutlined fontSize="inherit" />
          </Box>
          <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 500 }}>
            Saldo Kas Saat Ini
          </Typography>
          <Typography variant="h2" fontWeight="bold" mt={1}>
            {formatRupiah(kasInfo.saldoSaatIni)}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.7, mt: 1 }}>
            Diperbarui: {moment().format("DD MMMM YYYY, HH:mm")}
          </Typography>
        </Paper>

        {/* Stat Cards */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={statCardStyle}>
              <Box display="flex" alignItems="center" gap={1}>
                <TrendingUpOutlined sx={{ color: "#4caf50" }} />
                <Typography
                  variant="body2"
                  color={theme.palette.secondary[300]}
                >
                  Pendapatan
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color="#4caf50">
                {formatRupiah(kasInfo.pendapatan)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={statCardStyle}>
              <Box display="flex" alignItems="center" gap={1}>
                <AccountBalanceWalletOutlined sx={{ color: "#2196f3" }} />
                <Typography
                  variant="body2"
                  color={theme.palette.secondary[300]}
                >
                  Alokasi Pengeluaran
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color="#2196f3">
                {formatRupiah(kasInfo.alokasi)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={statCardStyle}>
              <Box display="flex" alignItems="center" gap={1}>
                <TrendingDownOutlined
                  sx={{ color: theme.palette.secondary[500] }}
                />
                <Typography
                  variant="body2"
                  color={theme.palette.secondary[300]}
                >
                  Pengeluaran Realisasi
                </Typography>
              </Box>
              <Typography
                variant="h4"
                fontWeight="bold"
                color={theme.palette.secondary[500]}
              >
                {formatRupiah(kasInfo.pengeluaran)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={statCardStyle}>
              <Box display="flex" alignItems="center" gap={1}>
                <ReceiptLongOutlined sx={{ color: "#ff9800" }} />
                <Typography
                  variant="body2"
                  color={theme.palette.secondary[300]}
                >
                  Selisih Pendapatan & Pengeluaran
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color="#ff9800">
                {formatRupiah(kasInfo.selisih)}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Box mt="2.5rem">
        <Typography
          variant="h5"
          fontWeight="600"
          color={theme.palette.secondary[100]}
          mb="1rem"
          display="flex"
          alignItems="center"
          gap={1}
        >
          <ReceiptLongOutlined /> Riwayat Flow Keuangan
        </Typography>

        <Paper sx={{ ...cardStyle, p: 0, overflow: "hidden" }}>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: theme.palette.background.alt,
                      fontSize: "13px",
                      width: 40,
                    }}
                  />
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: theme.palette.background.alt,
                      fontSize: "13px",
                    }}
                  >
                    Tanggal
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: theme.palette.background.alt,
                      fontSize: "13px",
                    }}
                  >
                    Keterangan
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: theme.palette.background.alt,
                      fontSize: "13px",
                    }}
                    align="center"
                  >
                    Tipe
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: theme.palette.background.alt,
                      fontSize: "13px",
                    }}
                    align="center"
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: theme.palette.background.alt,
                      fontSize: "13px",
                    }}
                    align="right"
                  >
                    Nominal
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: theme.palette.background.alt,
                      fontSize: "13px",
                    }}
                    align="right"
                  >
                    Aksi
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {riwayatGrouped.map((row) => {
                  console.log("ROW: ", row);

                  const hasItems = row.items && row.items.length > 0;
                  const isExpanded = !!expandedRows[row.id];

                  return (
                    <React.Fragment key={row.id}>
                      <TableRow
                        sx={{
                          "&:hover": {
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.04)"
                                : "rgba(0,0,0,0.02)",
                          },
                          transition: "background-color 0.15s ease",
                          cursor: hasItems ? "pointer" : "default",
                          ...(hasItems &&
                            isExpanded && {
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.03)"
                                  : "rgba(0,0,0,0.015)",
                            }),
                        }}
                        onClick={() => {
                          if (hasItems) {
                            setExpandedRows((prev) => ({
                              ...prev,
                              [row.id]: !prev[row.id],
                            }));
                          }
                        }}
                      >
                        <TableCell sx={{ width: 40, p: 0.5 }}>
                          {hasItems && (
                            <IconButton size="small">
                              {isExpanded ? (
                                <KeyboardArrowUp fontSize="small" />
                              ) : (
                                <KeyboardArrowDown fontSize="small" />
                              )}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell
                          sx={{ fontSize: "13px", whiteSpace: "nowrap" }}
                        >
                          {moment(row.tanggal).format("DD MMM YYYY")}
                        </TableCell>
                        <TableCell sx={{ fontSize: "13px" }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            {row.keterangan}
                            {hasItems && (
                              <Chip
                                label={`${row.items.length} item`}
                                size="small"
                                sx={{
                                  fontSize: "10px",
                                  height: 20,
                                  fontWeight: 600,
                                  backgroundColor:
                                    theme.palette.mode === "dark"
                                      ? "rgba(255,255,255,0.1)"
                                      : "rgba(0,0,0,0.08)",
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={
                              row.tipe === "pemasukan"
                                ? "Pemasukan"
                                : "Pengeluaran"
                            }
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: "12px",
                              color: "#fff",
                              backgroundColor:
                                row.tipe === "pemasukan"
                                  ? "#4caf50"
                                  : "#f44336",
                              minWidth: 65,
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={
                              row.status === "alokasi" ? "Alokasi" : "Realisasi"
                            }
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: "12px",
                              color: "#fff",
                              backgroundColor:
                                row.status === "alokasi"
                                  ? "#545554"
                                  : "#5e84c2",
                              minWidth: 65,
                            }}
                          />
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color:
                              row.tipe === "pemasukan" ? "#4caf50" : "#f44336",
                          }}
                        >
                          {row.tipe === "pemasukan" ? "+ " : "- "}
                          {formatRupiah(row.nominal)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color:
                              row.tipe === "pemasukan" ? "#4caf50" : "#f44336",
                          }}
                        >
                          {/* Tombol Cetak (Hanya untuk item status alokasi) */}
                          {row.status === "alokasi" && (
                            <IconButton
                              size="small"
                              onClick={() => handlePrintAlokasi(row)}
                              sx={{
                                ml: 1,
                                borderRadius: 8,
                                fontSize: 12,
                                color: "#FFF",
                                backgroundColor: theme.palette.info.main,
                                "&:hover": {
                                  backgroundColor: theme.palette.info.dark,
                                },
                              }}
                            >
                              <PrintOutlined fontSize="small" />
                              <Typography
                                variant="caption"
                                sx={{ ml: 0.5, fontWeight: 500 }}
                              >
                                Cetak
                              </Typography>
                            </IconButton>
                          )}

                          {/* Kondisi: Hanya tampilkan jika tipe 'pengeluaran' dan status 'alokasi' */}
                          {row.tipe === "pengeluaran" &&
                            row.status === "alokasi" && (
                              <>
                                {/* 1. Tombol Realisasi (Muncul di semua kondisi hasItems) */}
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    let ids;
                                    if (hasItems) {
                                      ids = row.items.map((item) =>
                                        item.id.toString(),
                                      );
                                    } else {
                                      ids = [row.singleItem.id];
                                    }
                                    console.log("button realisasi: ", ids);

                                    dispatch(realisasiListTransaksiWarkat({ids: ids}));
                                  }}
                                  sx={styles.btnRealisasi}
                                >
                                  <CheckBoxOutlined fontSize="small" />
                                  <Typography
                                    variant="caption"
                                    sx={{ ml: 0.5, fontWeight: 500 }}
                                  >
                                    Realisasi
                                  </Typography>
                                </IconButton>

                                {/* 2. Tombol Aksi Tambahan (Hanya muncul jika !hasItems) */}
                                {!hasItems && (
                                  <>
                                    {/* Tombol Hapus */}
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setSelectedTransaksi(row.singleItem);
                                        setOpenKonfirmasi(true);
                                      }}
                                      sx={styles.btnHapus}
                                    >
                                      <DeleteOutline fontSize="small" />
                                      <Typography
                                        variant="caption"
                                        sx={{ ml: 0.5, fontWeight: 500 }}
                                      >
                                        Hapus
                                      </Typography>
                                    </IconButton>

                                    {/* Tombol Ubah Tanggal */}
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setSelectedTransaksi(row.singleItem);
                                        setTanggalPengganti(
                                          moment(row.tanggal),
                                        );
                                        setOpenUbahTanggal(true);
                                      }}
                                      sx={styles.btnUbah}
                                    >
                                      <CalendarMonthOutlined fontSize="small" />
                                      <Typography
                                        variant="caption"
                                        sx={{ ml: 0.5, fontWeight: 500 }}
                                      >
                                        Ubah
                                      </Typography>
                                    </IconButton>
                                  </>
                                )}
                              </>
                            )}
                        </TableCell>
                      </TableRow>

                      {/* ── Expandable sub-items ─── */}
                      {hasItems && (
                        <TableRow>
                          <TableCell
                            colSpan={7}
                            sx={{
                              p: 0,
                              borderBottom: isExpanded ? undefined : "none",
                            }}
                          >
                            <Collapse
                              in={isExpanded}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box
                                sx={{
                                  py: 1,
                                  px: 2,
                                  ml: 5,
                                  mr: 2,
                                  my: 2,
                                  borderRadius: "8px",
                                  backgroundColor:
                                    theme.palette.mode === "dark"
                                      ? "rgba(255,255,255,0.03)"
                                      : "rgba(0,0,0,0.02)",
                                  border: `1px solid ${
                                    theme.palette.mode === "dark"
                                      ? "rgba(255,255,255,0.06)"
                                      : "rgba(0,0,0,0.06)"
                                  }`,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  fontWeight="600"
                                  color={theme.palette.secondary[300]}
                                  sx={{ display: "block", mb: 1 }}
                                >
                                  Detail Item{" "}
                                  {row.tipe === "pemasukan"
                                    ? "Pemasukan"
                                    : "Pengeluaran"}
                                </Typography>
                                {row.items.map((subItem, idx) => (
                                  <Box
                                    key={subItem.id || idx}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    sx={{
                                      py: 0.8,
                                      px: 1,
                                      borderBottom:
                                        idx < row.items.length - 1
                                          ? `1px solid ${
                                              theme.palette.mode === "dark"
                                                ? "rgba(255,255,255,0.06)"
                                                : "rgba(0,0,0,0.06)"
                                            }`
                                          : "none",
                                    }}
                                  >
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography
                                        variant="body2"
                                        fontSize="12px"
                                        fontWeight="500"
                                      >
                                        {subItem.nama}
                                      </Typography>
                                      {subItem.catatan &&
                                        subItem.catatan !== subItem.nama && (
                                          <Typography
                                            variant="caption"
                                            color={theme.palette.secondary[300]}
                                          >
                                            {subItem.catatan}
                                          </Typography>
                                        )}
                                      {subItem.nama_supplier && (
                                        <Typography
                                          variant="caption"
                                          display="block"
                                          color={theme.palette.secondary[300]}
                                        >
                                          Supplier: {subItem.nama_supplier}
                                        </Typography>
                                      )}
                                      {subItem.nama_perkiraan && (
                                        <Typography
                                          variant="caption"
                                          display="block"
                                          color={theme.palette.secondary[300]}
                                        >
                                          Perkiraan: {subItem.nama_perkiraan}
                                        </Typography>
                                      )}
                                    </Box>
                                    <Typography
                                      variant="body2"
                                      fontWeight="600"
                                      fontSize="12px"
                                      color={
                                        row.tipe === "pemasukan"
                                          ? "#4e504e"
                                          : "#f44336"
                                      }
                                      sx={{ whiteSpace: "nowrap", ml: 2 }}
                                    >
                                      {row.tipe === "pemasukan" ? "+ " : "- "}
                                      {formatRupiah(
                                        Number(subItem.nominal) || 0,
                                      )}
                                    </Typography>
                                    {row.tipe === "pengeluaran" &&
                                      row.status === "alokasi" && (
                                        <>
                                          <IconButton
                                            size="small"
                                            onClick={() => {
                                              setSelectedTransaksi(subItem);
                                              setOpenKonfirmasi(true);
                                            }}
                                            sx={{
                                              ml: 1,
                                              borderRadius: 8,
                                              fontSize: 12,
                                              color: "#fff",
                                              backgroundColor: "#f6041c",
                                              "&:hover": {
                                                backgroundColor:
                                                  "rgba(244,67,54,0.1)",
                                              },
                                            }}
                                          >
                                            <DeleteOutline fontSize="small" />{" "}
                                            Hapus
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            onClick={() => {
                                              setSelectedTransaksi(subItem);
                                              setTanggalPengganti(
                                                moment(subItem.tanggal),
                                              );
                                              setOpenUbahTanggal(true);
                                            }}
                                            sx={{
                                              ml: 1,
                                              borderRadius: 1,
                                              fontSize: 12,
                                              color: "#fff",
                                              backgroundColor:
                                                theme.palette.primary.dark,
                                              padding: "4px 8px",
                                              "&:hover": {
                                                backgroundColor:
                                                  theme.palette.primary.dark,
                                              },
                                            }}
                                          >
                                            <CalendarMonthOutlined fontSize="small" />
                                            <Typography
                                              variant="caption"
                                              sx={{ ml: 0.5, fontWeight: 500 }}
                                            >
                                              Ubah
                                            </Typography>
                                          </IconButton>
                                        </>
                                      )}
                                  </Box>
                                ))}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 3: Tambah Pengeluaran + Alokasi
          ══════════════════════════════════════════════════════════════════════ */}
      <Box mt="2.5rem">
        <Typography
          variant="h5"
          fontWeight="600"
          color={theme.palette.secondary[100]}
          mb="1rem"
          display="flex"
          alignItems="center"
          gap={1}
        >
          <AddCircleOutline /> Tambahkan Pengeluaran
        </Typography>

        <Grid container spacing={2}>
          {/* ── Kolom Kiri: Input Form ──────────────────────────────────── */}
          <Grid item xs={12} md={7}>
            <Paper sx={cardStyle}>
              <Tabs
                value={tabValue}
                onChange={(_, v) => setTabValue(v)}
                textColor="secondary"
                indicatorColor="secondary"
                sx={{
                  mb: 2,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "14px",
                  },
                }}
              >
                <Tab
                  icon={<PaymentOutlined />}
                  iconPosition="start"
                  label="Bayar Pengeluaran Terdaftar"
                />
                <Tab
                  icon={<CategoryOutlined />}
                  iconPosition="start"
                  label="Pengeluaran Lainnya"
                />
              </Tabs>

              <Divider sx={{ mb: 3 }} />

              {/* ── Tab 1: Bayar Pengeluaran Terdaftar ─────────────────────── */}
              {tabValue === 0 && (
                <Box>
                  <Typography
                    variant="body2"
                    color={theme.palette.secondary[300]}
                    mb={2}
                  >
                    Pilih transaksi terdaftar, lalu tambahkan ke alokasi.
                  </Typography>

                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => setOpenPilihTransaksi(true)}
                    sx={{
                      height: "56px",
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "14px",
                      borderColor: theme.palette.secondary.main,
                      color: theme.palette.secondary.main,
                      display: "flex",
                      justifyContent: "space-between",
                      px: 2,
                      "&:hover": {
                        backgroundColor: `${theme.palette.secondary.main}10`,
                        borderColor: theme.palette.secondary.dark,
                      },
                    }}
                  >
                    <span>
                      {(selectedTransaksi || selectedTransaksis.length > 0)
                        ? selectedTransaksis.length > 0 
                          ? `${selectedTransaksis.length} transaksi dipilih`
                          : `${selectedTransaksi.no_bukti_penerimaan} - ${selectedTransaksi.nama_supplier}`
                        : "Pilih Transaksi Pengeluaran"}
                    </span>
                    <PaymentOutlined />
                  </Button>

                  {(selectedTransaksi || selectedTransaksis.length > 0) && (
                    <Box mt={2} p={2} sx={{ backgroundColor: theme.palette.background.default, borderRadius: 2 }}>
                      {selectedTransaksis.length > 0 ? (
                        <Box>
                          <Typography variant="body2" fontWeight="bold" mb={1}>
                            {selectedTransaksis.length} Transaksi Dipilih
                          </Typography>
                          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ backgroundColor: theme.palette.background.alt }}>
                                  <TableCell sx={{ fontWeight: "bold", fontSize: "12px" }}>No. Bukti</TableCell>
                                  <TableCell sx={{ fontWeight: "bold", fontSize: "12px" }}>Supplier</TableCell>
                                  <TableCell sx={{ fontWeight: "bold", fontSize: "12px" }} align="right">Sisa Hutang</TableCell>
                                  <TableCell sx={{ fontWeight: "bold", fontSize: "12px" }} align="right">
                                    Nominal Bayar *
                                    <Typography component="span" color="error" sx={{ ml: 0.5 }}>Wajib</Typography>
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {selectedTransaksis.map((item, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell sx={{ fontSize: "12px" }}>{item.no_bukti_penerimaan}</TableCell>
                                    <TableCell sx={{ fontSize: "12px" }}>{item.nama_supplier}</TableCell>
                                    <TableCell sx={{ fontSize: "12px", fontWeight: "bold" }} align="right">
                                      {formatRupiah(parseInt(item.sisa_hutang) || 0)}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: "12px" }} align="right">
                                      <TextField
                                        size="small"
                                        value={nominalBayarMulti[item.no_bukti_penerimaan] || ""}
                                        onChange={(e) => {
                                          const value = e.target.value.replace(/\D/g, "");
                                          setNominalBayarMulti((prev) => ({
                                            ...prev,
                                            [item.no_bukti_penerimaan]: value ? parseInt(value) : 0,
                                          }));
                                        }}
                                        sx={{ width: 120 }}
                                        InputProps={{
                                          startAdornment: <InputAdornment position="start" sx={{ fontSize: "12px" }}>Rp</InputAdornment>,
                                        }}
                                      />
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      ) : (
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Tanggal Transaksi</Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {moment(selectedTransaksi.tanggal_transaksi).format("DD-MM-YYYY")}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Jatuh Tempo</Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {moment(selectedTransaksi.tanggal_transaksi).add(parseInt(selectedTransaksi.top) || 0, "days").format("DD-MM-YYYY")}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Nominal</Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {formatRupiah(parseInt(selectedTransaksi.nominal_penerimaan) || 0)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Sisa Hutang</Typography>
                          <Typography variant="body1" fontWeight="bold" color="error.main">
                            {formatRupiah(parseInt(selectedTransaksi.sisa_hutang) || 0)}
                          </Typography>
                        </Grid>
                      </Grid>
                      )}
                    </Box>
                  )}

                  {(selectedTransaksi || selectedTransaksis.length > 0) && (
                  <Grid container spacing={2} alignItems="center" mt={2}>
                    <Grid item xs={12} sm={8}>
                      <TextField
                        label="Nominal yang Dibayarkan"
                        fullWidth
                        size="medium"
                        value={nominalBayar}
                        disabled
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">Rp</InputAdornment>
                          ),
                        }}
                        placeholder="0"
                        helperText={selectedTransaksis.length > 0 ? "Isi nominal per item di tabel atas" : "Nominal diisi dari sisa hutang"}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={handleTambahAlokasiTerdaftar}
                        startIcon={<AddShoppingCartOutlined />}
                        sx={{
                          height: "56px",
                          textTransform: "none",
                          fontWeight: 600,
                          color: "white",
                          fontSize: "14px",
                          backgroundColor: theme.palette.secondary[500],
                          "&:hover": {
                            backgroundColor: theme.palette.secondary[600],
                          },
                        }}
                      >
                        Ke Alokasi
                      </Button>
                    </Grid>
                  </Grid>
                  )}

                  {selectedTransaksi && selectedTransaksis.length === 0 &&
                    (() => {
                      const rawNominal =
                        parseInt(
                          (nominalBayar || "0").replace(/\D/g, ""),
                          10,
                        ) || 0;
                      const tagihan =
                        parseInt(selectedTransaksi.sisa_hutang) || 0;
                      const selisih = tagihan - rawNominal;

                      if (rawNominal > tagihan) {
                        return (
                          <Alert
                            severity="error"
                            sx={{ mt: 2, borderRadius: "8px" }}
                          >
                            Nominal pembayaran{" "}
                            <strong>{formatRupiah(rawNominal)}</strong> melebihi
                            sisa hutang <strong>{formatRupiah(tagihan)}</strong>
                            . Maksimal yang bisa dibayarkan adalah{" "}
                            <strong>{formatRupiah(tagihan)}</strong>.
                          </Alert>
                        );
                      }
                      if (rawNominal > 0 && rawNominal < tagihan) {
                        return (
                          <Alert
                            severity="warning"
                            sx={{ mt: 2, borderRadius: "8px" }}
                          >
                            Pengeluaran terpilih:{" "}
                            <strong>
                              {selectedTransaksi.no_bukti_penerimaan} -{" "}
                              {selectedTransaksi.nama_supplier}
                            </strong>{" "}
                            — Sisa Hutang:{" "}
                            <strong>{formatRupiah(tagihan)}</strong>
                            <br />
                            Lakukan Kontra Bon sebesar{" "}
                            <strong>{formatRupiah(selisih)}</strong>
                          </Alert>
                        );
                      }
                      return (
                        <Alert
                          severity="info"
                          sx={{ mt: 2, borderRadius: "8px" }}
                        >
                          Pengeluaran terpilih:{" "}
                          <strong>
                            {selectedTransaksi.no_bukti_penerimaan} -{" "}
                            {selectedTransaksi.nama_supplier}
                          </strong>{" "}
                          — Sisa Hutang:{" "}
                          <strong>{formatRupiah(tagihan)}</strong>
                        </Alert>
                      );
                    })()}

                  {selectedTransaksis.length > 0 &&
                    (() => {
                      const errors = [];
                      const warnings = [];
                      const infos = [];

                      selectedTransaksis.forEach((item) => {
                        const nominal = nominalBayarMulti[item.no_bukti_penerimaan] || 0;
                        const tagihan = parseInt(item.sisa_hutang) || 0;

                        if (nominal > tagihan) {
                          errors.push({ no: item.no_bukti_penerimaan, nominal, tagihan });
                        } else if (nominal > 0 && nominal < tagihan) {
                          infos.push({ no: item.no_bukti_penerimaan, nominal, tagihan, selisih: tagihan - nominal, supplier: item.nama_supplier });
                        } else if (nominal === 0) {
                          infos.push({ no: item.no_bukti_penerimaan, supplier: item.nama_supplier, tagihan, belum: true });
                        }
                      });

                      if (errors.length > 0) {
                        return (
                          <Alert severity="error" sx={{ mt: 2, borderRadius: "8px" }}>
                            {errors.map((err, idx) => (
                              <div key={idx}>
                                Nominal <strong>{formatRupiah(err.nominal)}</strong> untuk {err.no} melebihi sisa hutang <strong>{formatRupiah(err.tagihan)}</strong>.
                              </div>
                            ))}
                          </Alert>
                        );
                      }

                      if (infos.length > 0) {
                        return (
                          <Alert severity="info" sx={{ mt: 2, borderRadius: "8px" }}>
                            {infos.map((info, idx) => (
                              <div key={idx}>
                                {info.belum 
                                  ? `${info.no} - ${info.supplier}: Sisa Hutang ${formatRupiah(info.tagihan)} (belum diisi)`
                                  : `${info.no} - ${info.supplier}: Sisa Hutang ${formatRupiah(info.tagihan)} — Bayar ${formatRupiah(info.nominal)} (Kontra Bon ${formatRupiah(info.selisih)})`
                                }
                              </div>
                            ))}
                          </Alert>
                        );
                      }

                      return null;
                    })()}
                </Box>
              )}

              {/* ── Tab 2: Pengeluaran Lainnya ─────────────────────────────── */}
              {tabValue === 1 && (
                <Box>
                  <Typography
                    variant="body2"
                    color={theme.palette.secondary[300]}
                    mb={2}
                  >
                    Isi data pengeluaran lainnya, lalu tambahkan ke alokasi.
                  </Typography>

                  <Autocomplete
                    value={selectedTipe}
                    onChange={(_, newVal) => setSelectedTipe(newVal)}
                    options={list_perkiraan}
                    getOptionLabel={(opt) =>
                      opt.nama_perkiraan
                        ? `${opt.kode_perkiraan} - ${opt.nama_perkiraan}`
                        : opt.kode_perkiraan || ""
                    }
                    filterOptions={(options, { inputValue }) => {
                      const lower = inputValue.toLowerCase();
                      return options.filter(
                        (opt) =>
                          (opt.nama_perkiraan || "")
                            .toLowerCase()
                            .includes(lower) ||
                          (opt.kode_perkiraan || "")
                            .toLowerCase()
                            .includes(lower),
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tipe Perkiraan"
                        placeholder="Cari nama perkiraan..."
                        fullWidth
                        size="medium"
                      />
                    )}
                    noOptionsText="Tipe perkiraan tidak ditemukan"
                    isOptionEqualToValue={(opt, val) =>
                      opt.kode_perkiraan === val.kode_perkiraan
                    }
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    label="Nama Pengeluaran"
                    fullWidth
                    size="medium"
                    value={namaTransaksi}
                    onChange={(e) => setNamaTransaksi(e.target.value)}
                    placeholder="Contoh: Beli ATK Kantor"
                    sx={{ mb: 2 }}
                  />

                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={8}>
                      <TextField
                        label="Nominal yang Dibayarkan"
                        fullWidth
                        size="medium"
                        value={nominalLainnya}
                        onChange={handleNominalChange(setNominalLainnya)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">Rp</InputAdornment>
                          ),
                        }}
                        placeholder="0"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={handleTambahAlokasiLainnya}
                        startIcon={<AddShoppingCartOutlined />}
                        sx={{
                          height: "56px",
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: "14px",
                          backgroundColor: theme.palette.secondary[500],
                          "&:hover": {
                            backgroundColor: theme.palette.secondary[600],
                          },
                        }}
                      >
                        Ke Alokasi
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* ── Kolom Kanan: Alokasi Pengeluaran ─────────────────────── */}
          <Grid item xs={12} md={5}>
            <Paper
              sx={{
                ...cardStyle,
                border: `2px solid ${
                  alokasi.length > 0
                    ? theme.palette.secondary[500]
                    : theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.08)"
                }`,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                minHeight: 350,
              }}
            >
              {/* Header Alokasi */}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Badge
                    badgeContent={alokasi.length}
                    color="error"
                    sx={{
                      "& .MuiBadge-badge": {
                        fontWeight: 700,
                      },
                    }}
                  >
                    <ChecklistOutlined
                      sx={{
                        color: theme.palette.secondary[500],
                        fontSize: 28,
                      }}
                    />
                  </Badge>
                  <Typography variant="h6" fontWeight="600" ml={0.5}>
                    Alokasi Pengeluaran
                  </Typography>
                </Box>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label="Tanggal Alokasi"
                    sx={{ width: "30%", mb: 2 }}
                    value={tanggalAlokasi}
                    onChange={(val) => setTanggalAlokasi(val)}
                    format="DD/MM/YYYY"
                    renderInput={(params) => (
                      <TextField {...params} fullWidth />
                    )}
                  />
                </LocalizationProvider>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Isi Alokasi */}
              <Box sx={{ flex: 1, overflowY: "auto", mb: 2 }}>
                {alokasi.length === 0 ? (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ py: 5, opacity: 0.5 }}
                  >
                    <ChecklistOutlined
                      sx={{ fontSize: 48, mb: 1, opacity: 0.4 }}
                    />
                    <Typography
                      variant="body2"
                      color={theme.palette.secondary[300]}
                      textAlign="center"
                    >
                      Alokasi masih kosong.
                      <br />
                      Tambahkan item dari form di samping.
                    </Typography>
                  </Box>
                ) : (
                  alokasi.map((item, index) => (
                    <Box
                      key={item.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 1.5,
                        mb: 1,
                        borderRadius: "8px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.04)"
                            : "rgba(0,0,0,0.02)",
                        border: `1px solid ${
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.08)"
                            : "rgba(0,0,0,0.06)"
                        }`,
                        transition: "all 0.15s ease",
                        "&:hover": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.07)"
                              : "rgba(0,0,0,0.04)",
                        },
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          mb={0.5}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              px: 1,
                              py: 0.2,
                              borderRadius: "4px",
                              fontWeight: 600,
                              fontSize: "10px",
                              color: "#fff",
                              backgroundColor:
                                item.sumber === "Terdaftar"
                                  ? "#2196f3"
                                  : "#ff9800",
                            }}
                          >
                            {item.sumber}
                          </Typography>
                          <Typography
                            variant="caption"
                            color={theme.palette.secondary[300]}
                          >
                            #{index + 1}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          fontWeight="500"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.keterangan}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color={theme.palette.secondary[500]}
                        >
                          {formatRupiah(item.nominal)}
                        </Typography>
                      </Box>
                      <Box display="flex" flexDirection="column" ml={1}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditAlokasi(item)}
                          sx={{
                            color: "#2196f3",
                            "&:hover": {
                              backgroundColor: "rgba(33,150,243,0.1)",
                            },
                          }}
                        >
                          <EditOutlined fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleHapusDariAlokasi(item.id)}
                          sx={{
                            color: "#f44336",
                            "&:hover": {
                              backgroundColor: "rgba(244,67,54,0.1)",
                            },
                          }}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>

              {/* Footer Alokasi */}
              {alokasi.length > 0 && (
                <>
                  <Divider sx={{ mb: 2 }} />
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                  >
                    <Typography variant="body1" fontWeight="600">
                      Total ({alokasi.length} item)
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      color={theme.palette.secondary[500]}
                    >
                      {formatRupiah(totalAlokasi)}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleProsesAlokasi}
                    startIcon={<SendOutlined />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      color: "white",
                      fontSize: "15px",
                      py: 1.5,
                      borderRadius: "10px",
                      backgroundColor: theme.palette.secondary[500],
                      "&:hover": {
                        backgroundColor: theme.palette.secondary[600],
                      },
                    }}
                  >
                    Proses Semua Pengeluaran
                  </Button>
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* ── Snackbar Feedback ───────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: "8px" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <FloatingAlert
        open={message.length > 0}
        onClose={handleCloseSnackbar}
        message={message}
        severity={status === "failed" ? "error" : status}
      />
      <KonfirmasiModal
        titleType={"Hapus"}
        subtitleType={"Menghapus"}
        dataWillProses={selectedTransaksi?.nama}
        open={openKonfirmasi}
        onClose={() => {
          setOpenKonfirmasi(false);
          setSelectedTransaksi(null);
          setTanggalPengganti(null);
        }}
        onSubmit={() => handleDelete()}
      />
      <UbahTanggalModal
        open={openUbahTanggal}
        onClose={() => {
          setOpenUbahTanggal(false);
          setSelectedTransaksi(null);
          setTanggalPengganti(null);
        }}
        onSubmit={handleUbahTanggal}
        selectedTransaksi={selectedTransaksi}
        tanggalPengganti={tanggalPengganti}
        setTanggalPengganti={setTanggalPengganti}
      />

      {/* ── Modal Pilih Transaksi ─────────────────────── */}
      <Dialog 
        open={openPilihTransaksi} 
        onClose={() => setOpenPilihTransaksi(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <PaymentOutlined color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Pilih Transaksi Pengeluaran
            </Typography>
          </Box>
          <IconButton onClick={() => setOpenPilihTransaksi(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box mb={3}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <DatePicker
                      label="Mulai"
                      value={startJatuhTempo}
                      onChange={(newValue) => setStartJatuhTempo(newValue)}
                      slotProps={{
                        textField: { size: "small", sx: { width: 160 } },
                      }}
                    />
                    <Typography>-</Typography>
                    <DatePicker
                      label="Akhir"
                      value={endJatuhTempo}
                      onChange={(newValue) => setEndJatuhTempo(newValue)}
                      slotProps={{
                        textField: { size: "small", sx: { width: 160 } },
                      }}
                    />
                  </Box>
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Cari Supplier atau No. Bukti"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button 
                  variant="outlined" 
                  onClick={handleResetFilterTransaksi}
                  fullWidth
                  sx={{ height: "40px" }}
                >
                  Reset Filter
                </Button>
              </Grid>
            </Grid>
          </Box>

          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.background.alt }}>
                  <TableCell padding="checkbox" sx={{ fontWeight: "bold" }}>
                    <Checkbox
                      indeterminate={selectedTransaksis.length > 0 && selectedTransaksis.length < filteredListTransaksi.length}
                      checked={filteredListTransaksi.length > 0 && selectedTransaksis.length === filteredListTransaksi.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTransaksis([...filteredListTransaksi]);
                        } else {
                          setSelectedTransaksis([]);
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>No. Bukti</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Tanggal Transaksi</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Supplier</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="right">Nominal</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="right">Sisa Hutang</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="center">TOP (Hari)</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="center">Jatuh Tempo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredListTransaksi.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">Tidak ada transaksi ditemukan</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredListTransaksi.map((item, index) => {
                    const tglTransaksi = moment(item.tanggal_transaksi);
                    const top = parseInt(item.top) || 0;
                    const tglJatuhTempo = tglTransaksi.clone().add(top, "days");
                    const today = moment();
                    const isLewat = tglJatuhTempo.isBefore(today, "day");
                    const isLunas = parseInt(item.sisa_hutang) === 0 || parseInt(item.sisa_hutang) < 0;
                    const isSelected = selectedTransaksis.some((t) => t.no_bukti_penerimaan === item.no_bukti_penerimaan);
                    
                    return (
                      <TableRow 
                        key={index} 
                        hover 
                        selected={isSelected}
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleToggleTransaksi(item)}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => handleToggleTransaksi(item)}
                          />
                        </TableCell>
                        <TableCell>{item.no_bukti_penerimaan || "-"}</TableCell>
                        <TableCell>{tglTransaksi.format("DD-MM-YYYY")}</TableCell>
                        <TableCell>{item.nama_supplier || "-"}</TableCell>
                        <TableCell align="right">{formatRupiah(parseInt(item.nominal_penerimaan) || 0)}</TableCell>
                        <TableCell align="right">
                          <Typography 
                            color={isLunas ? "success" : "error"} 
                            fontWeight={isLunas ? "normal" : "bold"}
                          >
                            {formatRupiah(parseInt(item.sisa_hutang) || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">{top}</TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={tglJatuhTempo.format("DD-MM-YYYY")}
                            size="small"
                            color={isLewat && !isLunas ? "error" : isLewat && isLunas ? "success" : "default"}
                            variant={isLewat && !isLunas ? "filled" : "outlined"}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Total: {filteredListTransaksi.length} transaksi | Selected: {selectedTransaksis.length}
            </Typography>
            <Button 
              variant="contained" 
              onClick={handlePilihMultiple}
              disabled={selectedTransaksis.length === 0}
              sx={{ textTransform: "none" }}
            >
              Pilih ({selectedTransaksis.length})
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            variant="outlined" 
            onClick={() => setOpenPilihTransaksi(false)}
            sx={{ textTransform: "none" }}
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KasBesar